import type {
  IframePoolOfferOdds,
  PoolOfferDetail,
  PoolOfferListItem,
  TierBadge,
  VolatilityLevel,
} from '../../../modules/api';
import type { CategoryPillItem } from '../../../modules/design-system';
import { DemoRipButton } from '../../../modules/demo';
import { BottomAction, MOBILE_RAIL_SPACER_HEIGHT } from './BottomAction';
import { isOfferAvailable } from './offerStatus';
import { FairnessTicketKeeper } from '../../../modules/fairness/ui';
import { OddsPanel, OddsPanelSkeleton } from './OddsPanel';
import { PackInfoCard } from './PackInfoCard';
import { Slider } from './Slider';
import { WhatsInsideButton } from '../WhatsInside';

/**
 * Grid layout for the modern pack-detail page.
 *
 * Mirrors the classic grid exactly (same container breakpoints, same slot
 * order) so future iterations only need to swap stub blocks for real
 * components — no surrounding-layout churn.
 *
 *   < 800px  → single column, `action` block at the end of the flow
 *   ≥ 800px  → two columns (hero | 360px sidebar); `action` moves into
 *              the sidebar.
 */

/** The locked buy button's copy (partner ask 2026-10-28). */
const UNAVAILABLE_LABEL = 'Pack unavailable';

export interface PackLayoutProps {
  /** Currently-centered offer's detail — drives info card, odds, buy price. */
  offer: PoolOfferDetail | null;
  /** Every pack in the active category — drives the slider slots. */
  categoryOffers: PoolOfferListItem[];
  /** Active offer id (slider centre). */
  activeOfferId: number | null;
  odds: IframePoolOfferOdds | null;
  maxForPurchaseCount: number | undefined;
  /** Opens the "What's inside" page; omitted while the pack's pool is empty. */
  onShowWhatsInside?: () => void;
  categories: CategoryPillItem[];
  activeCategoryId?: string;
  /** Header pill click — updates URL + active gameId. */
  onCategorySelect: (id: string) => void;
  /** Slider swipe — updates active offer in-page, keeps URL stable. */
  onSlideChange?: (offerId: number) => void;
  onTierSelect?: (poolOfferId: number) => void;
  quantity?: number;
  onQuantityChange?: (next: number) => void;
  onBuy?: () => void;
  /** Balance short of the picked packs — the buy button reads "Deposit to rip". */
  isShortOfFunds?: boolean;
  unitPrice?: number | string;
  volatility: VolatilityLevel;
  onVolatilityChange: (level: VolatilityLevel) => void;
  /** Free demo rip — `pack.demoMode` AND an embed_key in hand. */
  showDemo?: boolean;
  onOpenDemo?: () => void;
}

export function PackLayout({
  offer,
  categoryOffers,
  activeOfferId,
  odds,
  maxForPurchaseCount,
  onShowWhatsInside,
  categories: _categories,
  activeCategoryId: _activeCategoryId,
  onCategorySelect: _onCategorySelect,
  onSlideChange,
  quantity = 1,
  onQuantityChange,
  onBuy,
  isShortOfFunds = false,
  unitPrice = 0,
  volatility,
  onVolatilityChange,
  showDemo = false,
  onOpenDemo,
}: PackLayoutProps) {
  // Not purchasable (sold out = restocking): the action bar stays, locked, and
  // reads UNAVAILABLE_LABEL instead of the price; "What's inside" and the odds
  // picker lock too, the pack art carries the status badge (partner ask
  // 2026-10-28).
  const isUnavailable = offer !== null && !isOfferAvailable(offer.status);
  const unavailableLabel = isUnavailable ? UNAVAILABLE_LABEL : undefined;

  // Convert category-offers list to TierBadge[] so PackInfoCard's tier
  // selector can render one card per sibling pack. Clicking a card
  // fires `onSlideChange` (same handler the slider's swipe uses) so the
  // slider animates to that offer — and PackInfoCard, OddsPanel,
  // BottomAction all follow because they read from `activeOfferId`.
  //
  // Tier label = the offer's own `badgeName` (per-operator override aware,
  // flat field from ShortSerializer). The `suitableBadges` own-row lookup
  // stays as a fallback for older backends without the flat field; when
  // neither yields a name we fall back to the pack title so the card never
  // shows blank.
  const categoryOffersAsTiers: TierBadge[] = categoryOffers.map((o) => {
    const ownBadge =
      o.suitableBadges.find((b) => b.poolOfferId === o.id) ??
      o.suitableBadges.find((b) => b.isSelf);
    return {
      poolOfferId: o.id,
      name: o.badgeName || ownBadge?.name?.trim() || o.title,
      price: o.price,
      imageUrl: o.packImageUrl,
      subImageUrl: null,
      isSelf: activeOfferId !== null && o.id === activeOfferId,
    };
  });

  // Slider needs at least the current pack to render. When the category
  // index endpoint returns 0 offers (e.g. operator-scope filter excludes
  // siblings, or the URL `game_id` doesn't sit in any visible category),
  // synthesise a single-item list from `offer` so the centre pack still
  // shows. Slider already gates chevrons/dots/drag on `len > 1`, so the
  // synthesised single-item case renders as a static centred pack — no
  // controls, no peeks.
  const sliderOffers: PoolOfferListItem[] =
    categoryOffers.length > 0
      ? categoryOffers
      : offer
        ? [
            {
              id: offer.id,
              title: offer.title,
              price: offer.price,
              status: offer.status,
              isMultiple: false,
              packImageUrl: offer.packImageUrl,
              packImageAspect: offer.packImageAspect,
              packLogoImageUrl: offer.packLogoImageUrl,
              pedestalImageUrl: offer.pedestalImageUrl,
              suitableBadges: offer.suitableBadges,
              // Detail payload has no flat badge field — the synthesised
              // single slide never renders a tier row anyway.
              badgeName: null,
              operatorCurrency: offer.operatorCurrency,
              exchangeRate: offer.exchangeRate,
              demoMode: offer.demoMode,
            },
          ]
        : [];

  // One Max Pull block, two homes: the sidebar from 800px, the fixed buy dock
  // below it — so on a phone it is always in view (partner feedback
  // 2026-10-07: "docked at the bottom, like the button dock").
  const stats = odds ? (
    <OddsPanel
      odds={odds}
      volatility={volatility}
      onVolatilityChange={onVolatilityChange}
      isLocked={isUnavailable}
    />
  ) : (
    <OddsPanelSkeleton />
  );

  return (
    // `@container` lives on a wrapper with NO padding so its content-box
    // size (which is what `@container size` queries against) matches the
    // viewport. If we put `px-X` on the same element, the padding
    // shrinks the content-box and rules like
    // `@[800px]:grid-cols-...` stop firing until viewport ≈ 848 (800 +
    // 48 of padding) — a Tailwind 4 / CSS Container-Query gotcha.
    // Below 800px the page is a full-height column over the fixed dock (Max
    // Pull + buy row, see BottomAction); from 800px it is the two-column grid,
    // centred in the height left under the header — a tall screen (unfolded
    // Galaxy Fold, portrait monitor) no longer leaves an empty lower half.
    <div className="@container flex flex-1 flex-col">
      <div
        className="
          relative @[800px]:pb-8 @[600px]:px-6 @[900px]:px-8
          mx-auto w-full max-w-350
          flex flex-1 flex-col gap-2
          @[800px]:grid @[800px]:flex-none @[800px]:my-auto
          @[800px]:grid-cols-[minmax(0,1fr)_360px]
          @[800px]:gap-x-6 @[800px]:gap-y-2
          @[1200px]:grid-cols-[minmax(0,1fr)_400px]
        "
      >
        {/* MOBILE-ONLY demo entry (Figma mobile ref) — top-left under the
            sticky Header, floating OVER the slider/pack art rather than
            taking a row, so toggling demo mode never shifts the page. The
            left offset matches the grid gutter (20px, +24px padding from
            600px). z-30 clears the slider's swipe layer (z-25) so the tap
            lands on the button. Desktop shows the same button lower,
            inside the sidebar (see below) — this one is hidden there. */}
        {showDemo && onOpenDemo && (
          <div className="@[800px]:hidden absolute top-0 left-5 @[600px]:left-11 z-30">
            <DemoRipButton onClick={onOpenDemo} />
          </div>
        )}

        {/* HERO / SLIDER — left column, row 1.
            On tablet (600–800, single column) the slider bleeds BOTH
            sides of the grid's px-6 so the peek packs get clipped by the
            viewport edge — same edge-cut look as mobile — instead of a
            hard cut 24px inside the page. On desktop it bleeds left only
            (px-6 at 800–900, px-8 above) so it runs from the viewport's
            left edge to the sidebar gap, matching Figma 543:2118 where
            the slider area starts flush against the frame's left edge.
            Width is grown by the same amount so the right edge stays
            where the grid put it. */}
        <div
          className="
            relative
            @[600px]:-mx-6 @[600px]:w-[calc(100%+3rem)]
            @[800px]:col-start-1 @[800px]:row-start-1
            @[800px]:mr-0 @[800px]:-ml-6 @[800px]:w-[calc(100%+1.5rem)]
            @[900px]:-ml-8 @[900px]:w-[calc(100%+2rem)]
          "
        >
          <Slider
            offers={sliderOffers}
            activeOfferId={activeOfferId}
            {...(onSlideChange ? { onSlideChange } : {})}
          />
        </div>

        {/* Mobile "What's inside" pill right under the pack title (partner
            Loom 2026-10-06) — the pool moved to its own page. Desktop shows
            the same pill in the sidebar under the info card. */}
        {onShowWhatsInside && (
          <div className="flex justify-center pt-1 @[800px]:hidden">
            <WhatsInsideButton onClick={onShowWhatsInside} disabled={isUnavailable} />
          </div>
        )}

        {/* SIDEBAR — right column. On desktop a peek pack from the
            slider may slide *behind* the sidebar (no hard clip at the
            slider edge), so the sidebar is given a higher z-index and
            `relative` to overlay any peek silhouette that bleeds into
            its column. The cards themselves are GLASS_SURFACE-blurred,
            so the peek reads as a soft shape under the sidebar.

            z-30 sits below the sticky header (z-40). The OddsPanel popover
            is `absolute z-50` but trapped inside THIS stacking context, so it
            would render under the header (the "My Packs" pill overlapped it).
            While the popover is open the OddsPanel flags `data-odds-popover-open`
            here, lifting the sidebar above the header (z-50) just for that
            interaction — reverting to z-30 on close so the header stays on top
            during normal scroll. */}
        {/* `row-span-2` on desktop lets the sidebar run past the slider's row,
            so the left column stays tight (no empty gap below the slider) when
            the sidebar is taller. Mobile keeps single-column flow — row-span
            only activates ≥ 800px. */}
        {/* `h-0` on desktop: the sidebar hangs down from the slider's top
            without adding to the grid's height, so the slider alone centres
            the grid. A row that comes and goes per pack (the demo entry)
            then only moves the sidebar's own bottom — before, it moved the
            pack and the sidebar ~22px on every switch. */}
        <aside
          className="
            mt-auto flex flex-col gap-2 @[800px]:mt-0 @[800px]:h-0
            @[800px]:col-start-2 @[800px]:row-start-1 @[800px]:row-span-2
            @[800px]:relative @[800px]:z-30
            @[800px]:has-[[data-odds-popover-open]]:z-50
          "
        >
          <PackInfoCard
            title={offer?.title ?? ''}
            tiers={categoryOffersAsTiers}
            packLogoImageUrl={offer?.packLogoImageUrl ?? null}
            description={offer?.description ?? null}
            {...(offer ? { buybackPercent: offer.buybackPercent } : {})}
            {...(onSlideChange ? { onTierSelect: onSlideChange } : {})}
          />

          {onShowWhatsInside && (
            // Desktop: full column width and the demo pill's 48px height, so it
            // lines up with the other sidebar rows instead of floating as a
            // small pill (2026-10-09).
            <div className="hidden @[800px]:block">
              <WhatsInsideButton
                onClick={onShowWhatsInside}
                disabled={isUnavailable}
                className="w-full h-12!"
              />
            </div>
          )}

          <div className="hidden @[800px]:block">{stats}</div>
          <FairnessTicketKeeper offerId={offer?.id} />

          {/* Desktop demo entry — between odds and the buy dock, matching
              the sidebar ordering in the Figma reference. Mobile shows the
              same button up near the Header instead (see above). */}
          {showDemo && onOpenDemo && (
            <div className="hidden @[800px]:block">
              <DemoRipButton onClick={onOpenDemo} fullWidth />
            </div>
          )}

          {/* `pb-8`: the grid's own bottom padding no longer reaches the
              sidebar hanging past it, so the gap under Buy lives here. */}
          <div className="hidden @[800px]:block @[800px]:pb-8">
            <BottomAction
              quantity={quantity}
              onQuantityChange={onQuantityChange ?? (() => {})}
              onBuy={onBuy ?? (() => {})}
              isShortOfFunds={isShortOfFunds}
              unitPrice={unitPrice}
              mobileStats={stats}
              {...(maxForPurchaseCount !== undefined ? { maxQty: maxForPurchaseCount } : {})}
              {...(unavailableLabel ? { unavailableLabel } : {})}
            />
          </div>
        </aside>

        {/* Mobile spacer — reserves the buy dock's height (80px plus the
            operator's dock margin/padding) under the in-flow content so it
            never slides under the portal'd `MobileBar` (which lives in
            `<body>` and is fixed to the viewport bottom). The bar itself
            is rendered exactly once from the desktop `<BottomAction>`
            mount inside the sidebar — its `MobileBar` child portals to
            body globally, so a second mount here would create a
            duplicate dock in `<body>`. */}
        <div
          className="@[800px]:hidden"
          style={{ height: MOBILE_RAIL_SPACER_HEIGHT }}
          aria-hidden
        />
      </div>
    </div>
  );
}
